import { useState, useEffect } from 'react';
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useLocation, Link } from "wouter";
import { useToast } from "@/hooks/use-toast";
import { apiRequest } from "@/lib/queryClient";
import logoUrl from "@assets/edufocal-logo-min-50292a59_1758905778671.png";

export default function PartnerLogin() {
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [partner, setPartner] = useState<any>(null);
  
  const [, setLocation] = useLocation();
  const { toast } = useToast();

  // Check if already authenticated
  useEffect(() => {
    const checkAuth = async () => {
      try {
        const response = await fetch('/api/ref/me');
        if (response.ok) {
          const data = await response.json();
          setPartner(data.partner);
          setLocation('/ref/dashboard');
        }
      } catch (error) {
        // Not authenticated, that's fine
      }
    };
    checkAuth();
  }, [setLocation]);

  const handleChange = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (isSubmitting) return;
    
    setIsSubmitting(true);
    try {
      const response = await apiRequest('POST', '/api/ref/login', formData);
      const data = await response.json();
      setPartner(data.partner);
      toast({
        title: "Welcome back!",
        description: "You've successfully signed in to your partner account.",
      });
      setLocation('/ref/dashboard');
    } catch (error: any) {
      toast({
        title: "Sign in failed",
        description: error.message || "Invalid email or password. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-chart-2 via-chart-2/90 to-primary flex items-center justify-center p-4 sm:p-6 md:p-8">
      <Card className="w-full max-w-md mx-auto">
        <CardHeader className="text-center space-y-4 sm:space-y-6 pb-6 sm:pb-8 p-4 sm:p-6">
          <div className="flex justify-center">
            <img src={logoUrl} alt="EduFocal" className="h-10 sm:h-12 w-auto" data-testid="img-partner-login-logo" />
          </div>
          
          <div className="space-y-2">
            <h1 className="text-2xl sm:text-3xl font-bold text-foreground" data-testid="text-partner-login-headline">
              Partner Portal
            </h1>
            <p className="text-muted-foreground text-sm sm:text-base" data-testid="text-partner-login-subtitle">
              Sign in to access your referral dashboard
            </p>
          </div>
        </CardHeader>
        
        <CardContent className="space-y-4 sm:space-y-6 p-4 sm:p-6">
          <form onSubmit={handleSubmit} className="space-y-4 sm:space-y-6">
            <div className="space-y-2">
              <Label htmlFor="email" className="text-sm sm:text-base">Email</Label>
              <Input
                id="email"
                type="email"
                placeholder="Enter your email"
                value={formData.email}
                onChange={(e) => handleChange('email', e.target.value)}
                required
                className="h-12 text-base"
                data-testid="input-partner-email"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="password" className="text-sm sm:text-base">Password</Label>
              <Input
                id="password"
                type="password"
                placeholder="Enter your password"
                value={formData.password}
                onChange={(e) => handleChange('password', e.target.value)}
                required
                className="h-12 text-base"
                data-testid="input-partner-password"
              />
            </div>
            
            <Button 
              type="submit" 
              disabled={isSubmitting}
              className="w-full text-base sm:text-lg py-3 sm:py-4 min-h-12 sm:min-h-14 mt-6"
              data-testid="button-partner-sign-in"
            >
              {isSubmitting ? "Signing in..." : "Sign In"}
            </Button>
          </form>
          
          <div className="text-center text-sm text-muted-foreground pt-4 border-t">
            Don't have a partner account?{' '}
            <Link href="/ref/signup">
              <Button
                variant="ghost"
                className="p-0 h-auto text-primary font-medium"
                data-testid="link-partner-signup"
              >
                Sign up here
              </Button>
            </Link>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
